import React, { Component } from 'react';
import { BRAND } from '../config/branding';
import axiosClient from '../api/axiosClient';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details to state
    this.setState({
      error,
      errorInfo
    });

    // Log error to backend
    this.logErrorToBackend(error, errorInfo);

    // Also log to console for development
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  logErrorToBackend = async (error, errorInfo) => {
    try {
      const errorData = {
        message: error?.toString() || 'Unknown error',
        stack: error?.stack || 'No stack trace',
        componentStack: errorInfo?.componentStack || 'No component stack',
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        user: JSON.parse(localStorage.getItem('user') || '{}')?.username || 'anonymous'
      };

      const response = await axiosClient.post('/system/log-client-error', errorData);

      if (response.data?.error_id) {
        this.setState({ errorId: response.data.error_id });
      }
    } catch (logError) {
      // Silently fail - don't want error logging to cause more errors
      console.error('Failed to log error to backend:', logError);
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleClearStorage = () => {
    if (window.confirm('This will clear all cached data and reload the page. Continue?')) {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/';
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          padding: '20px'
        }}>
          <div style={{
            maxWidth: '600px',
            width: '100%',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            padding: '40px',
            textAlign: 'center'
          }}>
            {/* Error Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 20px',
              backgroundColor: '#dc3545',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={{ fontSize: '48px', color: 'white' }}>!</span>
            </div>

            {/* Error Title */}
            <h1 style={{
              margin: '0 0 10px 0',
              fontSize: '28px',
              color: '#333',
              fontWeight: '700'
            }}>
              Oops! Something went wrong
            </h1>

            {/* Error Message */}
            <p style={{
              color: '#666',
              fontSize: '16px',
              lineHeight: '1.5',
              marginBottom: '20px'
            }}>
              We encountered an unexpected error. Don't worry, this has been logged and our team will investigate.
            </p>

            {/* Error ID */}
            {this.state.errorId && (
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '15px',
                borderRadius: '4px',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#666'
              }}>
                <strong>Error ID:</strong> {this.state.errorId}
                <br />
                <small>Please reference this ID if you contact support</small>
              </div>
            )}

            {/* Error Details (collapsed by default) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{
                marginBottom: '20px',
                textAlign: 'left',
                backgroundColor: '#f8f9fa',
                padding: '15px',
                borderRadius: '4px'
              }}>
                <summary style={{
                  cursor: 'pointer',
                  fontWeight: '600',
                  marginBottom: '10px',
                  color: '#dc3545'
                }}>
                  Error Details (Development Mode)
                </summary>
                <div style={{
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  color: '#333',
                  backgroundColor: '#fff',
                  padding: '10px',
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '200px'
                }}>
                  <strong>Error:</strong> {this.state.error.toString()}
                  <br /><br />
                  <strong>Stack:</strong>
                  <pre style={{ margin: '5px 0', whiteSpace: 'pre-wrap' }}>
                    {this.state.error.stack}
                  </pre>
                  {this.state.errorInfo?.componentStack && (
                    <>
                      <strong>Component Stack:</strong>
                      <pre style={{ margin: '5px 0', whiteSpace: 'pre-wrap' }}>
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </>
                  )}
                </div>
              </details>
            )}

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={this.handleReload}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#218838'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#28a745'}
              >
                Reload Page
              </button>

              <button
                onClick={this.handleGoHome}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
              >
                Go to Dashboard
              </button>

              <button
                onClick={this.handleClearStorage}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#5a6268'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#6c757d'}
              >
                Clear Cache & Reload
              </button>
            </div>

            {/* Footer */}
            <p style={{
              marginTop: '30px',
              fontSize: '14px',
              color: '#999'
            }}>
              {BRAND.name} - We're sorry for the inconvenience
            </p>
          </div>
        </div>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;
